import MapIcon from '@mui/icons-material/Map';
import { Box, Stack, Typography } from '@mui/material';
import { grey } from '@mui/material/colors';
import { useEffect } from 'react';
import { useMixpanel } from 'react-mixpanel-browser';

export type MapType = 'main' | 'public' | 'geofence' | 'historicalTrack';

export type MapUnavailableProps = {
    mapType: MapType;
    source: 'mapboxGlUnsupported' | 'errorBoundary';
    reason?: string;
    errorMessage?: string;
};

type BrowserKind = 'chromium' | 'firefox' | 'safari' | 'unknown';

const detectBrowser = (userAgent?: string): BrowserKind => {
    const ua = userAgent?.toLowerCase() ?? '';

    if (!ua) return 'unknown';
    if (/firefox|fxios/.test(ua)) return 'firefox';
    if (/safari/.test(ua) && !/chrome|chromium|crios|edg|edge|opr|opera|brave/.test(ua)) return 'safari';
    if (/chrome|chromium|crios|edg|edge|opr|opera|brave/.test(ua)) return 'chromium';

    return 'unknown';
};

export const getMapUnavailableInstructions = (userAgent?: string): string => {
    switch (detectBrowser(userAgent)) {
        case 'chromium':
            return 'In Chrome, Edge, Brave, or Opera: Settings → System → "Use graphics acceleration when available", then restart the browser.';
        case 'firefox':
            return 'In Firefox: Settings → General → Performance → uncheck "Use recommended performance settings" → check "Use hardware acceleration when available", then restart Firefox.';
        case 'safari':
            return 'In Safari: Update macOS and Safari to the latest version. If WebGL was disabled in Develop menu or Experimental Features, re-enable it.';
        default:
            return 'Enable hardware or graphics acceleration in your browser settings, then restart the browser.';
    }
};

export const MapUnavailable = ({ mapType, source, reason, errorMessage }: MapUnavailableProps) => {
    const mixpanel = useMixpanel();

    const showWebGlInstructions = source === 'mapboxGlUnsupported';

    useEffect(() => {
        mixpanel?.track('Map Unavailable Shown', { mapType, source, reason, errorMessage });
    }, [mixpanel, mapType, source, reason, errorMessage]);

    const browserInstructions = getMapUnavailableInstructions(
        typeof navigator === 'undefined' ? undefined : navigator.userAgent
    );

    return (
        <Box display="flex" flexDirection="column" flex={1} height="100%">
            <Stack
                sx={{
                    flex: 1,
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    p: 2,
                    gap: 1,
                    bgcolor: grey[100],
                }}
            >
                <MapIcon sx={{ fontSize: { '@300': 48, '@': 32 }, color: grey[500] }} />
                <Typography variant="h6" sx={{ fontSize: { '@300': '1.25rem', '@': '1rem' } }}>
                    Map unavailable
                </Typography>
                {showWebGlInstructions ? (
                    <>
                        <Typography variant="body2" sx={{ maxWidth: 480 }}>
                            Your browser can&apos;t display the map because WebGL isn&apos;t available. This is usually
                            because hardware acceleration is disabled in your browser settings.
                        </Typography>
                        <Typography
                            variant="caption"
                            sx={{ color: grey[600], maxWidth: 480, display: { '@300': 'block', '@': 'none' } }}
                        >
                            {browserInstructions}
                        </Typography>
                    </>
                ) : (
                    <>
                        <Typography variant="body2" sx={{ maxWidth: 480 }}>
                            There was an error displaying the map. Try restarting your browser or logging out and back
                            in again.
                        </Typography>
                        {errorMessage && (
                            <Typography
                                variant="caption"
                                sx={{ color: grey[600], maxWidth: 480, display: { '@300': 'block', '@': 'none' } }}
                            >
                                Error:{' '}
                                <Box component="span" sx={{ fontFamily: 'monospace' }}>
                                    {errorMessage}
                                </Box>
                            </Typography>
                        )}
                    </>
                )}
            </Stack>
        </Box>
    );
};
